import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useTheme } from '../../context/ThemeContext';
import {
  Users,
  Calendar,
  TrendingUp,
  MessageSquare,
  ArrowUp,
  ArrowDown,
  Clock,
  Phone,
  Mail,
  Bell,
  RefreshCw,
  ChevronRight,
  BarChart3,
  Target,
  Zap
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'blue', theme }) => {
  const colors = {
    blue: 'from-blue-500 to-cyan-600',
    green: 'from-green-500 to-emerald-600',
    purple: 'from-purple-500 to-pink-600',
    orange: 'from-orange-500 to-amber-600'
  };

  const cardBg = theme === 'dark' ? 'bg-slate-800/50 border-slate-700 hover:border-slate-600' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const textMuted = theme === 'dark' ? 'text-gray-500' : 'text-gray-500';

  return (
    <Card className={`${cardBg} transition-colors`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className={`${textSecondary} text-sm mb-1`}>{title}</p>
            <p className={`text-3xl font-bold ${textPrimary}`}>{value}</p>
            {subtitle && <p className={`${textMuted} text-xs mt-1`}>{subtitle}</p>}
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${
                trend >= 0 ? 'text-green-500' : 'text-red-500'
              }`}>
                {trend >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                <span>{Math.abs(trend)}% vs mes anterior</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 bg-gradient-to-br ${colors[color]} rounded-xl flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MiniChart = ({ data, dataKey, color = '#3b82f6', theme }) => {
  const maxValue = Math.max(...data.map(d => d[dataKey]), 1);
  const textMuted = theme === 'dark' ? 'text-gray-500' : 'text-gray-400';
  
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((item, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full rounded-t transition-all hover:opacity-80"
            style={{ 
              height: `${(item[dataKey] / maxValue) * 100}%`,
              minHeight: item[dataKey] > 0 ? '4px' : '2px',
              backgroundColor: item[dataKey] > 0 ? color : (theme === 'dark' ? '#374151' : '#e5e7eb')
            }}
          />
          <span className={`text-[10px] ${textMuted}`}>{item.date}</span>
        </div>
      ))}
    </div>
  );
};

const SourceBadge = ({ source, count, total, theme }) => {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
  const sourceLabels = {
    whatsapp: { label: 'WhatsApp', color: 'bg-green-500' },
    whatsapp_twilio: { label: 'WhatsApp', color: 'bg-green-500' },
    meta_ads: { label: 'Meta Ads', color: 'bg-blue-500' },
    tiktok: { label: 'TikTok', color: 'bg-pink-500' },
    organic: { label: 'Orgánico', color: 'bg-purple-500' },
    website: { label: 'Sitio Web', color: 'bg-cyan-500' },
  };
  
  const config = sourceLabels[source] || { label: source, color: 'bg-gray-500' };
  const textSecondary = theme === 'dark' ? 'text-gray-300' : 'text-gray-700';
  const textMuted = theme === 'dark' ? 'text-gray-500' : 'text-gray-400';
  
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${config.color}`} />
        <span className={`text-sm ${textSecondary}`}>{config.label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{count}</span>
        <span className={`text-xs ${textMuted}`}>({percentage}%)</span>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingReminders, setSendingReminders] = useState(false);
  const { theme } = useTheme();

  // Theme classes
  const cardBg = theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const textMuted = theme === 'dark' ? 'text-gray-500' : 'text-gray-500';
  const itemBg = theme === 'dark' ? 'bg-slate-700/30' : 'bg-gray-50';
  const dividerColor = theme === 'dark' ? 'divide-slate-700' : 'divide-gray-200';
  const borderColor = theme === 'dark' ? 'border-slate-600' : 'border-gray-300';

  const loadDashboard = async () => {
    try {
      // Get clinicId from localStorage or use default for main clinic
      let clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      
      // Fallback to main clinic if not logged in
      if (!clinicId) {
        clinicId = '0476280a-82ca-4713-bb61-0a9ade950bcd';
        localStorage.setItem('clinicId', clinicId);
      }
      
      const res = await fetch(`${API}/clinic/${clinicId}/dashboard`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      } else {
        console.error('Dashboard API error:', res.status);
      }
    } catch (err) {
      console.error('Error loading dashboard:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      
      const res = await fetch(`${API}/clinic/${clinicId}/send-reminders`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        alert(`✅ ${data.reminders_sent} recordatorios enviados`);
      }
    } catch (err) {
      console.error('Error sending reminders:', err);
    }
    setSendingReminders(false);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  const totalSources = Object.values(stats?.source_breakdown || {}).reduce((a, b) => a + b, 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${textPrimary}`}>Dashboard</h1>
            <p className={textMuted}>Resumen de tu clínica</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadDashboard}
              className={borderColor}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button
              size="sm"
              onClick={sendReminders}
              disabled={sendingReminders}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white"
            >
              <Bell className="w-4 h-4 mr-2" />
              {sendingReminders ? 'Enviando...' : 'Enviar Recordatorios'}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Leads Totales"
            value={stats?.total_leads || 0}
            subtitle={`${stats?.new_leads_today || 0} hoy`}
            icon={Users}
            trend={stats?.leads_growth}
            color="blue"
            theme={theme}
          />
          <StatCard
            title="Citas Este Mes"
            value={stats?.appointments_week || 0}
            subtitle={`${stats?.appointments_today || 0} hoy`}
            icon={Calendar}
            color="green"
            theme={theme}
          />
          <StatCard
            title="Tasa de Conversión"
            value={`${stats?.conversion_rate || 0}%`}
            subtitle="Leads → Citas"
            icon={Target}
            color="purple"
            theme={theme}
          />
          <StatCard
            title="Conversaciones"
            value={stats?.total_conversations || 0}
            subtitle="Este mes"
            icon={MessageSquare}
            color="orange"
            theme={theme}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Leads Chart */}
          <Card className={cardBg}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-lg flex items-center gap-2 ${textPrimary}`}>
                <BarChart3 className="w-5 h-5 text-blue-400" />
                Leads (últimos 7 días)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.daily_leads && (
                <MiniChart data={stats.daily_leads} dataKey="leads" color="#3b82f6" theme={theme} />
              )}
            </CardContent>
          </Card>

          {/* Appointments Chart */}
          <Card className={cardBg}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-lg flex items-center gap-2 ${textPrimary}`}>
                <Calendar className="w-5 h-5 text-green-400" />
                Citas (últimos 7 días)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.daily_appointments && (
                <MiniChart data={stats.daily_appointments} dataKey="appointments" color="#22c55e" theme={theme} />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Source Breakdown */}
          <Card className={cardBg}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-lg flex items-center gap-2 ${textPrimary}`}>
                <Zap className="w-5 h-5 text-yellow-400" />
                Fuentes de Leads
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.source_breakdown && Object.keys(stats.source_breakdown).length > 0 ? (
                <div className={`divide-y ${dividerColor}`}>
                  {Object.entries(stats.source_breakdown).map(([source, count]) => (
                    <SourceBadge 
                      key={source} 
                      source={source} 
                      count={count} 
                      total={totalSources}
                      theme={theme}
                    />
                  ))}
                </div>
              ) : (
                <p className={`${textMuted} text-sm text-center py-4`}>
                  Sin datos de fuentes aún
                </p>
              )}
            </CardContent>
          </Card>

          {/* Today's Appointments */}
          <Card className={cardBg}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-lg flex items-center gap-2 ${textPrimary}`}>
                <Clock className="w-5 h-5 text-cyan-400" />
                Citas de Hoy
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.todays_appointments?.length > 0 ? (
                <div className="space-y-3">
                  {stats.todays_appointments.slice(0, 4).map((apt, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-2 ${itemBg} rounded-lg`}>
                      <div>
                        <p className={`text-sm font-medium ${textPrimary}`}>{apt.patient_name}</p>
                        <p className={`text-xs ${textMuted}`}>{apt.service}</p>
                      </div>
                      <span className="text-sm text-cyan-500">
                        {new Date(apt.appointment_date).toLocaleTimeString('es-MX', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`${textMuted} text-sm text-center py-4`}>
                  Sin citas programadas para hoy
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recent Leads */}
          <Card className={cardBg}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-lg flex items-center gap-2 ${textPrimary}`}>
                <Users className="w-5 h-5 text-purple-400" />
                Leads Recientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.recent_leads?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_leads.slice(0, 4).map((lead, idx) => (
                    <div key={idx} className={`flex items-center justify-between p-2 ${itemBg} rounded-lg`}>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-xs font-bold text-white">
                          {lead.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${textPrimary}`}>{lead.name || 'Sin nombre'}</p>
                          <p className={`text-xs ${textMuted}`}>{lead.source || 'desconocido'}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        lead.status === 'appointment_scheduled' ? 'bg-green-500/20 text-green-500' :
                        lead.status === 'contacted' ? 'bg-blue-500/20 text-blue-500' :
                        'bg-gray-500/20 text-gray-500'
                      }`}>
                        {lead.status === 'appointment_scheduled' ? 'Agendado' :
                         lead.status === 'contacted' ? 'Contactado' : 'Nuevo'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={`${textMuted} text-sm text-center py-4`}>
                  Sin leads registrados aún
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
